import defaultConfig from '../data/supabase.js';
import {SERVER_MESSAGES} from '../libs/startState.js';
import {sendSignup} from './Signup.js';
import {dismissOfflineNotice} from './OfflineNotice.js';

const TOP_N = 5; // the start screen shows 5 rows (menu-frame-scaling)
// the game starts (offline) if start_run hasn't answered by then...
const START_TIMEOUT_MS = 5000;
// ...but the request carries on this long, and a token it brings back late
// still counts: a phone downloading the game's art at the same moment can be
// slow to answer, and its run should not be lost for that
const LATE_START_MS = 60000;
// a run that ends before its late start has answered waits this long for it
const START_WAIT_MS = 10000;
// submit and rank give up after this, so the end panel never waits on a hung request
const END_TIMEOUT_MS = 8000;
// A finished run is tried at most 1 + BACKOFF_MS.length times by its own
// page, these far apart, each try with END_TIMEOUT_MS, plus once at once
// whenever the browser comes back online or the tab back into view (no more
// than one such pass per EVENT_PASS_MS). One that still hasn't gone is tried
// on the next page load (PLAY AGAIN, or the next visit), and once more when
// the connection is seen to be back (an online event, or another save or the
// board loading); after failed passes on MAX_PASS_FAILS page loads it is dropped.
const BACKOFF_MS = [1000, 3000, 10000, 30000];
const EVENT_PASS_MS = 10000;
// a kept run is dropped after failed outbox passes on this many page loads
// (the page-load pass, or one set off by the connection coming back): a page
// counts at most one, so a brief outage can't use them all up at once, and
// its own page's backoff tries don't count
const MAX_PASS_FAILS = 3;
// answers that mean "not now" rather than "no": tried again like a network failure
const RETRY_STATUSES = [408, 429];
/** localStorage: the outbox, finished runs not yet sent, oldest first. */
export const UNSENT_KEY = 'receipthunt.unsentScores';
const OUTBOX_MAX_RUNS = 10;
// submit_run turns down a run that took more than 7200s from start_run: a
// kept run older than this is dropped rather than sent to be refused
export const UNSENT_MAX_AGE_MS = 115 * 60 * 1000;

const TIMED_OUT = Symbol('timed out');

// What start_run raises (SQLSTATE 22023) for a start the player can fix or
// must wait out. Anything else is treated as the network being unavailable.
const START_ERROR_CODES = [...Object.keys(SERVER_MESSAGES), 'rate_limited'];

/**
 * Talks to the leaderboard.
 *
 * A run is bracketed by two calls: startRun() before play, which has the
 * server record when the run began, which dog was picked and which work email
 * it belongs to, and submit() afterwards. The score is computed server-side
 * from the per-level receipts and bonus seconds and checked against what that
 * dog could possibly have done in the time elapsed, so the client is never
 * trusted to report a total. Both are Postgres functions -- see
 * supabase/migrations for the checks.
 */
export class Leaderboard {
  /**
   * @param {Object} [options]
   * @param {{url: string, anonKey: string}} [options.config] Supabase project
   * @param {Function} [options.fetch] fetch implementation, for tests; looked
   *   up on globalThis at call time when omitted
   * @param {Number} [options.startTimeoutMs] how long startRun waits
   * @param {Function} [options.signup] sends a started run's sign-up to
   *   HubSpot (Signup.js), replaceable in tests
   * @param {Number} [options.lateStartMs] how long a timed-out start_run may
   *   still bring a token back
   * @param {Number} [options.startWaitMs] how long submit waits for a late start
   * @param {Number[]} [options.backoffMs] pauses between a score's tries
   * @param {Storage|null} [options.storage] localStorage-like, for tests; null for none
   * @param {Function} [options.dismissNotice] takes the offline notice down
   *   when a late token means the run will count after all
   * @param {Function} [options.now] clock, for tests
   */
  constructor(options = {}) {
    this.config = options.config || defaultConfig;
    this.fetchImpl = options.fetch || null;
    this.startTimeoutMs = options.startTimeoutMs || START_TIMEOUT_MS;
    this.lateStartMs = options.lateStartMs || LATE_START_MS;
    this.startWaitMs = options.startWaitMs || START_WAIT_MS;
    this.backoffMs = options.backoffMs || BACKOFF_MS;
    this.signup = options.signup || sendSignup;
    this.dismissNotice = options.dismissNotice || dismissOfflineNotice;
    this.now = options.now || Date.now;
    this.storageOption = 'storage' in options ? {value: options.storage} : null;
    this.runToken = null;
    // which startRun is the latest: a late answer to an older one is ignored
    this.startSeq = 0;
    // resolves with the token (or null) once the latest start_run settles,
    // while it is still in flight after startRun gave up waiting
    this.pendingStart = null;
    // the latest started run's player and start time, for the outbox
    this.started = null;
    // the token of this page's own submitted run, for the end panel
    this.submittedToken = null;
    // the outbox, where there is no localStorage to keep it in
    this.memoryRuns = [];
    this.landedListeners = new Set();
    // one save request at a time: every try queues here
    this.queue = Promise.resolve();
    // while held (the loading screen is downloading the art) no try goes out
    this.held = false;
    this.heldWaiters = [];
    // a run's tries wait on this to stop early once it leaves the outbox
    this.goneWaiters = new Map();
    this.lastEventPass = -Infinity;
    this.passing = false;
    // kept runs that have already had their one failure counted on this page
    this.failCountedThisPage = new Set();
    this.unwatch = null;
    // the page-load pass over what earlier pages left, while it runs
    this.loadPass = null;
    // a run that ended while its start_run was still out: it is sent if the
    // token comes back (keepLateStart). {seq, entry} with no token yet.
    this.parked = null;
    // runs this page finished: its passes don't count against them
    this.ownTokens = new Set();
  }

  /**
   * The browser's localStorage, or null where there is none (node, private
   * mode, blocked storage).
   * @returns {Storage|null}
   * @private
   */
  get storage() {
    if (this.storageOption) {
      return this.storageOption.value;
    }
    try {
      return globalThis.localStorage || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * forgetRun
   * The current run won't be submitted (a quit, or a game that doesn't go on
   * the board): drops its token, and any token a late start_run would bring.
   */
  forgetRun() {
    this.startSeq += 1;
    this.runToken = null;
    this.pendingStart = null;
    this.parked = null;
  }

  /**
   * setFetch
   * @param {Function|null} fn replacement fetch, or null for the global one
   */
  setFetch(fn) {
    this.fetchImpl = fn;
  }

  /**
   * configured
   * @returns {Boolean} whether a Supabase project has been set up yet
   */
  get configured() {
    return Boolean(this.config.url && this.config.anonKey);
  }

  /**
   * call
   * @param {String} path REST path
   * @param {Object} [options] fetch options
   * @param {Number} [timeoutMs] abort the request after this long
   * @returns {Promise<Object>} the parsed JSON body
   * @throws {Error} on a non-2xx response, with `status` and `body` (the
   *   parsed error body, or null) attached; on a timeout or network failure
   * @private
   */
  async call(path, options, timeoutMs) {
    const doFetch = this.fetchImpl || globalThis.fetch.bind(globalThis);
    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    try {
      const response = await doFetch(`${this.config.url}${path}`, Object.assign({
        // a publishable key goes in apikey only; it isn't a JWT
        headers: {
          'Content-Type': 'application/json',
          apikey: this.config.anonKey
        }
      }, options, controller ? {signal: controller.signal} : {}));

      if (!response.ok) {
        let body = null;
        try {
          body = await response.json();
        } catch (e) {
          body = null;
        }
        const error = new Error(`${path} responded ${response.status}`);
        error.status = response.status;
        error.body = body;
        throw error;
      }
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * startRun
   * Asks the server to open a run. Without this the score can't be submitted,
   * which is what stops a score being posted without playing. The server
   * checks the work email and the rate limit; the caller decides what to do
   * with each failure code.
   * @param {Object} player
   * @param {String} player.name Player name
   * @param {String} player.dog The dog they picked
   * @param {String} player.email Work email
   * @param {Boolean} player.consent whether they ticked marketing consent
   * @param {String} [player.website] honeypot field; a real player leaves it empty
   * @returns {Promise<{ok: true, token: String}|{ok: false, code: String}>}
   *   code is work_email_required, invalid_email, rate_limited or network
   */
  async startRun({name, dog, email, consent, website = ''}) {
    this.forgetRun();
    const seq = this.startSeq;
    if (!this.configured) {
      return {ok: false, code: 'network'};
    }
    this.started = {name, dog, at: this.now()};

    const request = this.call('/rest/v1/rpc/start_run', {
      method: 'POST',
      body: JSON.stringify({
        player_name: name,
        player_dog: dog,
        player_email: email,
        marketing_consent: Boolean(consent),
        website: website || ''
      })
    }, this.lateStartMs).then((token) => {
      if (typeof token !== 'string' || !token) {
        throw new Error('start_run answered without a token');
      }
      return token;
    });
    // The game doesn't wait past startTimeoutMs, but the request isn't
    // cancelled: a token that comes back later is still this run's.
    let timer = null;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => resolve(TIMED_OUT), this.startTimeoutMs);
    });

    try {
      const token = await Promise.race([request, timeout]);
      clearTimeout(timer);
      if (token === TIMED_OUT) {
        this.keepLateStart(request, seq, {email, name, consent, website});
        this.sendTokenlessSignup({email, name, consent, website});
        return {ok: false, code: 'network'};
      }
      this.runToken = token;
      this.sendStartedSignup({email, name, consent, website, token});
      return {ok: true, token};
    } catch (e) {
      clearTimeout(timer);
      const message = e && e.body && e.body.message;
      const code = START_ERROR_CODES.find((c) => c === message) || 'network';
      if (code === 'network') {
        this.sendTokenlessSignup({email, name, consent, website});
      }
      return {ok: false, code};
    }
  }

  /**
   * After startRun gave up waiting: a token the request still brings back,
   * if no newer start has begun, becomes the run's token, so the score can
   * be submitted after all. The offline notice comes down, as the score will
   * now count, and the sign-up goes again with the token.
   * @param {Promise<String>} request the start_run call
   * @param {Number} seq the startRun it belongs to
   * @param {Object} player for the sign-up
   * @private
   */
  keepLateStart(request, seq, player) {
    const pending = request.then((token) => {
      if (seq !== this.startSeq) {
        return null;
      }
      this.sendStartedSignup(Object.assign({token}, player));
      const parked = this.parked;
      if (parked && parked.seq === seq) {
        // the run is over already and submit stopped waiting: send it now
        this.parked = null;
        this.sendRun(Object.assign({token}, parked.entry));
      } else {
        this.runToken = token;
      }
      try {
        this.dismissNotice();
      } catch (e) {
        // the notice is a nicety; the token is what matters
      }
      return token;
    }, () => null);
    this.pendingStart = pending;
    pending.then(() => {
      if (this.pendingStart === pending) {
        this.pendingStart = null;
      }
    });
  }

  /**
   * After an accepted start, not awaited: the game starts at once.
   * sendSignup skips it if this tab already sent this email (PLAY AGAIN),
   * and the Worker sends each email once. A run with the honeypot filled
   * is a bot's, so its address is not sent.
   * @private
   */
  sendStartedSignup({email, name, consent, website, token}) {
    if (website) {
      return;
    }
    try {
      this.signup({email, name, consent: Boolean(consent), token});
    } catch (e) {
      // never let the sign-up turn a started run into a failed one
    }
  }

  /**
   * The database couldn't be reached (yet), so the run plays offline. The
   * sign-up still goes to the Worker without a token; only a Worker with
   * HUBSPOT_SIGNUP_WITHOUT_RUN on (the playtest) passes it to HubSpot.
   * A start the database refused (bad email, rate limited) sends nothing.
   * @private
   */
  sendTokenlessSignup({email, name, consent, website}) {
    if (website) {
      return;
    }
    try {
      this.signup({email, name, consent: Boolean(consent), token: null});
    } catch (err) {
      // never let the sign-up affect the start
    }
  }

  /**
   * submit
   * Sends the finished run. If start_run is still on its way (it answered
   * after the game gave up on it), waits a little for its token first. The
   * run goes into the outbox before the first try and stays there until the
   * server has answered for it; a try lost to the network is tried again a
   * few times (sendWithBackoff), then on the browser coming back online
   * (watchOutbox) and on the next page load. Nothing in the game waits on
   * this beyond the end panel's placing line.
   * @param {{toSubmitArgs: Function, score: Number}} runResult the finished run
   * @returns {Promise<Object|null>} {rank, total, score, shareId} if the
   *   server accepted the run, null if it didn't or couldn't be reached (yet:
   *   onLanded reports a later landing)
   */
  async submit(runResult) {
    if (!this.configured || !runResult) {
      return null;
    }
    const args = runResult.toSubmitArgs();
    const started = this.started || {};
    const entry = {
      receipts_by_level: args.receipts_by_level,
      bonus_by_level: args.bonus_by_level,
      startedAt: started.at || this.now(),
      name: started.name,
      dog: started.dog,
      score: runResult.score
    };
    if (!this.runToken && this.pendingStart) {
      const seq = this.startSeq;
      await this.waitFor(this.pendingStart, this.startWaitMs);
      if (!this.runToken && this.pendingStart && seq === this.startSeq) {
        // Still no answer: the end panel stops waiting, but the run is parked
        // and goes the moment the token comes back (keepLateStart). Only in
        // memory: a page that goes (PLAY AGAIN) takes its start_run with it.
        this.parked = {seq, entry};
        return null;
      }
    }
    if (!this.runToken) {
      return null;
    }

    const token = this.runToken;
    this.runToken = null; // a token is good for one submission
    return this.sendRun(Object.assign({token}, entry));
  }

  /**
   * This page's finished run, with its token: into the outbox before the
   * first try, then sendWithBackoff.
   * @param {Object} entry the run, with its token
   * @returns {Promise<Object|null>} as submit
   * @private
   */
  sendRun(entry) {
    this.submittedToken = entry.token;
    this.ownTokens.add(entry.token);
    this.keepUnsent(entry);
    return this.sendWithBackoff(entry);
  }

  /**
   * Try a run, and again after each of backoffMs while the network keeps
   * failing it. Stops as soon as it leaves the outbox (landed or turned down,
   * here or by another try), so no timer outlives it.
   * @param {Object} entry an outbox run
   * @returns {Promise<Object|null>} as submit
   * @private
   */
  async sendWithBackoff(entry) {
    for (let attempt = 0; attempt <= this.backoffMs.length; attempt += 1) {
      if (attempt) {
        await this.waitFor(this.whenGone(entry.token), this.backoffMs[attempt - 1]);
      }
      const result = await this.attempt(entry);
      if (result.state !== 'failed') {
        return result.placing || null;
      }
    }
    return null;
  }

  /**
   * One try of one outbox run, queued behind any other save request and held
   * while the loading screen is up. Takes the run out of the outbox once the
   * server has answered for it: a row (accepted; onLanded hears of it), no
   * rows (turned down) or a 4xx.
   *
   * submit_run spends the token before it checks the run, so a try that
   * timed out may still have recorded it; the next try then gets no rows.
   * Nothing the browser may call can look a run up by its token, so that
   * case is taken like a refusal: the run leaves the outbox, as sending it
   * again could only get no rows again.
   * @param {Object} entry
   * @returns {Promise<{state: String, placing: (Object|undefined)}>} state is
   *   landed, gone (turned down, or no longer in the outbox) or failed (the
   *   network, a timeout or a 5xx: worth another try)
   * @private
   */
  attempt(entry) {
    const run = this.queue.then(async () => {
      await this.whenReleased();
      if (!this.readOutbox().some((e) => e.token === entry.token)) {
        return {state: 'gone'};
      }
      let rows;
      try {
        rows = await this.call('/rest/v1/rpc/submit_run', {
          method: 'POST',
          body: JSON.stringify({
            run_token: entry.token,
            receipts_by_level: entry.receipts_by_level,
            bonus_by_level: entry.bonus_by_level
          })
        }, END_TIMEOUT_MS);
      } catch (e) {
        if (e && e.status >= 400 && e.status < 500 && RETRY_STATUSES.indexOf(e.status) === -1) {
          this.forgetUnsent(entry.token);
          return {state: 'gone'};
        }
        return {state: 'failed'};
      }
      this.forgetUnsent(entry.token);
      // no rows back means the run was turned down
      const row = Array.isArray(rows) && rows[0];
      if (!row) {
        return {state: 'gone'};
      }
      const placing = {
        rank: row.rank,
        total: row.total,
        score: row.score,
        shareId: row.share_id
      };
      this.landed(entry.token, placing);
      // the connection works: anything else kept gets its try now
      this.connected();
      return {state: 'landed', placing};
    });
    this.queue = run.catch(() => null);
    return run.catch(() => ({state: 'failed'}));
  }

  /**
   * onLanded
   * @param {Function} fn called with (token, {rank, total, score, shareId})
   *   whenever a run from the outbox is accepted, however late
   * @returns {Function} stops listening
   */
  onLanded(fn) {
    this.landedListeners.add(fn);
    return () => this.landedListeners.delete(fn);
  }

  /**
   * @param {String} token
   * @param {Object} placing
   * @private
   */
  landed(token, placing) {
    this.landedListeners.forEach((fn) => {
      try {
        fn(token, placing);
      } catch (e) {
        // a listener's failure is not the submission's
      }
    });
  }

  /**
   * holdOutbox
   * The loading screen is downloading the game's art: no save request may
   * compete with it. Tries wait (without a timer) until releaseOutbox.
   */
  holdOutbox() {
    this.held = true;
  }

  /**
   * releaseOutbox
   * The game is up, or the start screen has its art in: tries may go.
   */
  releaseOutbox() {
    this.held = false;
    this.heldWaiters.splice(0).forEach((resolve) => resolve());
  }

  /**
   * @returns {Promise} resolves once the outbox isn't held
   * @private
   */
  whenReleased() {
    return this.held ? new Promise((resolve) => this.heldWaiters.push(resolve)) : Promise.resolve();
  }

  /**
   * @param {String} token
   * @returns {Promise} resolves once that run leaves the outbox
   * @private
   */
  whenGone(token) {
    return new Promise((resolve) => {
      const list = this.goneWaiters.get(token) || [];
      list.push(resolve);
      this.goneWaiters.set(token, list);
    });
  }

  /**
   * watchOutbox
   * On page load (PLAY AGAIN reloads the page too): each run earlier pages
   * left in the outbox (the phone was offline at the end, PLAY AGAIN or a
   * closed tab cut a submission short) is tried once, oldest first, ahead of
   * the start screen's board (Menu waits a moment for it: whenLoadPassDone).
   * While the page is open the browser coming back online, the tab coming
   * back into view, or any other save or board load getting through
   * (connected) sets off one try of everything still kept, at most once per
   * EVENT_PASS_MS. A run is dropped after failed passes on MAX_PASS_FAILS page loads.
   * Not awaited by the caller.
   * @param {EventTarget} [win] where 'online' fires (window)
   * @param {Document} [doc] where 'visibilitychange' fires (document)
   * @returns {Promise} the load pass
   */
  watchOutbox(win = globalThis.window, doc = globalThis.document) {
    if (!this.unwatch) {
      const online = () => this.eventPass();
      const visible = () => {
        if (!doc || doc.visibilityState !== 'hidden') {
          this.eventPass();
        }
      };
      try {
        if (win) win.addEventListener('online', online);
        if (doc) doc.addEventListener('visibilitychange', visible);
      } catch (e) {
        // the tries on load and at the end of a run still go
      }
      this.unwatch = () => {
        try {
          if (win) win.removeEventListener('online', online);
          if (doc) doc.removeEventListener('visibilitychange', visible);
        } catch (e) {
          // nothing to take off
        }
      };
    }
    const done = this.pass().catch(() => null).then(() => {
      if (this.loadPass === done) {
        this.loadPass = null;
      }
    });
    this.loadPass = done;
    return done;
  }

  /**
   * whenLoadPassDone
   * @param {Number} ms the longest to wait
   * @returns {Promise} resolves once the page-load pass is over, or after ms
   */
  whenLoadPassDone(ms) {
    return this.loadPass ? this.waitFor(this.loadPass, ms) : Promise.resolve();
  }

  /**
   * stopOutbox
   * Takes the listeners off (tests; the page never needs it).
   */
  stopOutbox() {
    if (this.unwatch) {
      this.unwatch();
      this.unwatch = null;
    }
  }

  /**
   * A pass for the online and visibility events, at most one per EVENT_PASS_MS.
   * @returns {Promise|null}
   * @private
   */
  eventPass() {
    const now = this.now();
    if (now - this.lastEventPass < EVENT_PASS_MS || !this.readOutbox().length) {
      return null;
    }
    this.lastEventPass = now;
    return this.pass();
  }

  /**
   * connected
   * Something just got through to the database (a save, the board): the
   * connection works, so anything still kept gets its try now, unless a pass
   * is already going (at most one per EVENT_PASS_MS, as for the events).
   * @returns {Promise|null}
   */
  connected() {
    return this.passing ? null : this.eventPass();
  }

  /**
   * One try of every run in the outbox, oldest first (runs too old for
   * submit_run are dropped as it is read). A failed try counts against a run
   * (passFails, kept with it across pages) unless this page finished it, and
   * a run is dropped after MAX_PASS_FAILS.
   * @returns {Promise}
   * @private
   */
  async pass() {
    if (!this.configured || this.passing) {
      return;
    }
    this.passing = true;
    try {
      this.writeOutbox(this.readOutbox());
      for (const entry of this.readOutbox()) {
        const result = await this.attempt(entry);
        if (result.state === 'failed' && !this.ownTokens.has(entry.token)) {
          this.countPassFail(entry.token);
        }
      }
    } finally {
      this.passing = false;
    }
  }

  /**
   * @param {String} token
   * @private
   */
  countPassFail(token) {
    if (this.failCountedThisPage.has(token)) {
      return;
    }
    this.failCountedThisPage.add(token);
    const runs = this.readOutbox();
    const entry = runs.find((e) => e.token === token);
    if (!entry) {
      return;
    }
    const fails = (entry.passFails || 0) + 1;
    if (fails >= MAX_PASS_FAILS) {
      this.forgetUnsent(token);
      return;
    }
    this.writeOutbox(runs.map((e) => (e.token === token ? Object.assign({}, e, {passFails: fails}) : e)));
  }

  /**
   * The outbox, oldest first, without runs too old to send: from
   * localStorage, or from memory where there is none (private mode, blocked
   * storage). Unreadable runs are left out.
   * @returns {Object[]}
   * @private
   */
  readOutbox() {
    let runs = this.memoryRuns;
    try {
      const store = this.storage;
      if (store) {
        const box = JSON.parse(store.getItem(UNSENT_KEY) || 'null');
        runs = (Array.isArray(box) ? box : []).concat(this.memoryRuns);
      }
    } catch (e) {
      runs = this.memoryRuns;
    }
    const now = this.now();
    const seen = new Set();
    return runs.filter((entry) => {
      const ok = entry && typeof entry.token === 'string' && !seen.has(entry.token) &&
        Array.isArray(entry.receipts_by_level) && Array.isArray(entry.bonus_by_level) &&
        typeof entry.startedAt === 'number' && now - entry.startedAt < UNSENT_MAX_AGE_MS;
      if (ok) {
        seen.add(entry.token);
      }
      return ok;
    });
  }

  /**
   * Save the outbox, the newest OUTBOX_MAX_RUNS only: to localStorage, or to
   * memory if that fails.
   * @param {Object[]} runs oldest first
   * @private
   */
  writeOutbox(runs) {
    const kept = runs.slice(-OUTBOX_MAX_RUNS);
    try {
      const store = this.storage;
      if (!store) {
        throw new Error('no storage');
      }
      if (kept.length) {
        store.setItem(UNSENT_KEY, JSON.stringify(kept));
      } else {
        store.removeItem(UNSENT_KEY);
      }
      this.memoryRuns = [];
    } catch (e) {
      // the game must play without storage (or it is full): the outbox lasts
      // as long as the page, and a stale copy left in storage is cleared so
      // it isn't read back alongside this one
      try {
        this.storage.removeItem(UNSENT_KEY);
      } catch (err) {
        // readOutbox drops the duplicates
      }
      this.memoryRuns = kept;
    }
  }

  /**
   * Put a run in the outbox, last (replacing one with the same token).
   * @param {Object} entry
   * @private
   */
  keepUnsent(entry) {
    this.writeOutbox(this.readOutbox().filter((e) => e.token !== entry.token).concat([entry]));
  }

  /**
   * Take a run out of the outbox, and stop its waiting tries.
   * @param {String} token
   * @private
   */
  forgetUnsent(token) {
    this.writeOutbox(this.readOutbox().filter((e) => e.token !== token));
    (this.goneWaiters.get(token) || []).forEach((resolve) => resolve());
    this.goneWaiters.delete(token);
  }

  /**
   * Wait for a promise, but no longer than ms.
   * @param {Promise} promise
   * @param {Number} ms
   * @returns {Promise}
   * @private
   */
  waitFor(promise, ms) {
    let timer = null;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    });
    return Promise.race([promise, timeout]).then((value) => {
      clearTimeout(timer);
      return value;
    });
  }

  /**
   * top
   * @returns {Promise<Array>} the highest scores, best first
   */
  async top() {
    if (!this.configured) {
      return [];
    }
    try {
      const rows = await this.call(
        `/rest/v1/leaderboard?select=name,dog,score,won,level_reached&order=score.desc,created_at.asc&limit=${TOP_N}`
      );
      // the board loaded, so the connection works: try anything still kept
      this.connected();
      return rows;
    } catch (e) {
      return [];
    }
  }

  /**
   * dogTotals
   * Every point each dog has earned, across everyone's runs. Dogs nobody has
   * played yet aren't in the list.
   * @returns {Promise<Array>} [{dog, points, runs}], most points first
   */
  async dogTotals() {
    if (!this.configured) {
      return [];
    }
    try {
      return await this.call('/rest/v1/rpc/dog_totals', {
        method: 'POST',
        body: '{}'
      });
    } catch (e) {
      return [];
    }
  }

  /**
   * rankFor
   * Where a score sits in the whole board, not just the top five. Counted by
   * the database rather than by pulling every row down.
   * @param {Number} score
   * @returns {Promise<Object|null>} {rank, total}
   */
  async rankFor(score) {
    if (!this.configured) {
      return null;
    }
    try {
      const rows = await this.call('/rest/v1/rpc/rank_for_score', {
        method: 'POST',
        body: JSON.stringify({for_score: score})
      }, END_TIMEOUT_MS);
      return rows[0] || null;
    } catch (e) {
      return null;
    }
  }
}

export default new Leaderboard();
